import {
  fetchAdminBookingContext,
  insertDemoRequest,
  type DemoProduct,
  type DemoRequestKind,
} from "@a/demoRequests";
import { isRateLimitError } from "@a/leads";
import type { AvailableSlot } from "@/types/booking.types";
import { BookingSlotPicker } from "@c/shared/BookingSlotPicker";
import { DateField } from "@c/shared/DateField";
import { SegChip, SegGroup } from "@c/shared/design/Segmented";
import { FieldLabel } from "@c/shared/FieldLabel";
import { Turnstile, isTurnstileEnabled } from "@c/shared/Turnstile";
import type { ListingCategory } from "@/types/tour/tour.types";
import { LISTING_CATEGORIES } from "@u/listingCategories";
import {
  LEAD_LANGUAGES,
  toLeadLanguage,
  type LeadLanguage,
} from "@u/leadStatus";
import { useMutation, useQuery } from "@tanstack/react-query";
import { cn } from "@u/cn";
import Button from "@ui/button";
import { Input } from "@ui/input";
import { Panel } from "@ui/panel";
import { Textarea } from "@ui/textarea";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

/** Tramos de superficie = los tramos de precio de la guía de cotización. */
const AREA_RANGES = ["lt80", "80_150", "150_300", "gt300"] as const;
type AreaRange = (typeof AREA_RANGES)[number];
/** Volumen: una puntual, varias, o cartera recurrente (pack mensual). */
const QUOTE_VOLUMES = ["one", "few", "recurring"] as const;
type QuoteVolume = (typeof QUOTE_VOLUMES)[number];
/** Estado del inmueble — cambia el tiempo en sitio. Solo real_estate. */
const QUOTE_CONDITIONS = ["empty", "furnished", "works"] as const;
type QuoteCondition = (typeof QUOTE_CONDITIONS)[number];

interface QuoteDetails {
  spaceType: ListingCategory;
  areaRange: AreaRange | "";
  location: string;
  spaceCount: string;
  volume: QuoteVolume | "";
  condition: QuoteCondition | "";
}

/**
 * Renderiza los datos del inmueble como bloque de texto al tope de `message`.
 * Decisión deliberada: cero columnas nuevas en `demo_requests` y cero redeploy
 * de `submit-public-form` — el volumen de cotizaciones no justifica schema.
 * ponytail: si algún día hay que filtrar/agregar por estos campos, migran a
 * una columna `quote_details jsonb`.
 */
function buildQuoteBlock(
  q: QuoteDetails,
  t: (k: string) => string,
): string {
  const rows: [string, string][] = [
    [t("quote_field_space_type"), t(`listing_category_${q.spaceType}`)],
    ...(q.areaRange
      ? ([[t("quote_field_area"), t(`quote_area_${q.areaRange}`)]] as [string, string][])
      : []),
    [t("quote_field_location"), q.location.trim()],
    ...(q.spaceCount.trim()
      ? ([[t("quote_field_spaces"), q.spaceCount.trim()]] as [string, string][])
      : []),
    ...(q.volume
      ? ([[t("quote_field_volume"), t(`quote_volume_${q.volume}`)]] as [string, string][])
      : []),
    ...(q.condition
      ? ([[t("quote_field_condition"), t(`quote_condition_${q.condition}`)]] as [string, string][])
      : []),
  ];
  return [
    `[${t("demo_kind_quote")}]`,
    ...rows.map(([k, v]) => `${k}: ${v}`),
  ].join("\n");
}

interface DemoRequestFormProps {
  /** Which surface opened the form — 'home' | '360' | 'book' … */
  source?: string;
  /** Preselect the product(s) of interest. */
  product?: DemoProduct;
  /** Renders a Cancel button (e.g. inside the modal). Omit on the page. */
  onCancel?: () => void;
  /** Notified when the form flips to its success state (modal title sync). */
  onSuccessChange?: (success: boolean) => void;
  className?: string;
}

/**
 * Shared demo / contact request form. Owns its state, validation, captcha and
 * the `demo_requests` mutation, plus the success state. Reused by both the
 * NiceModal wrapper (`DemoRequestModal`) and the standalone page (`BookDemoPage`)
 * so the lead capture never drifts between surfaces.
 */
export function DemoRequestForm({
  source,
  product = "360",
  onCancel,
  onSuccessChange,
  className,
}: DemoRequestFormProps) {
  const { t, i18n } = useTranslation();

  // Qué agenda el visitante: demo del producto o simplemente una conversación.
  const [kind, setKind] = useState<DemoRequestKind>("demo");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  // Un solo producto: el interés queda fijo (el selector se oculta abajo).
  const [interests, setInterests] = useState<DemoProduct[]>([product]);
  const toggleInterest = (v: DemoProduct) =>
    setInterests((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
    );
  const [preferredDate, setPreferredDate] = useState("");
  // Slot real contra la agenda del admin (motor de booking); null = sin elegir.
  const [slot, setSlot] = useState<AvailableSlot | null>(null);
  const [language, setLanguage] = useState<LeadLanguage | "">(
    toLeadLanguage(i18n.language) ?? "",
  );
  const [message, setMessage] = useState("");
  // Datos del inmueble para cotizar (solo kind='quote'). No tienen columnas
  // propias: se renderizan dentro de `message` — ver buildQuoteBlock.
  const [spaceType, setSpaceType] = useState<ListingCategory>("real_estate");
  const [areaRange, setAreaRange] = useState<AreaRange | "">("");
  const [location, setLocation] = useState("");
  const [spaceCount, setSpaceCount] = useState("");
  const [volume, setVolume] = useState<QuoteVolume | "">("");
  const [condition, setCondition] = useState<QuoteCondition | "">("");
  const [success, setSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  // En 'quote' el bloque de datos del inmueble encabeza el mensaje libre.
  const freeMessage = message.trim();
  const composedMessage =
    kind === "quote"
      ? [
          buildQuoteBlock(
            { spaceType, areaRange, location, spaceCount, volume, condition },
            t,
          ),
          freeMessage,
        ]
          .filter(Boolean)
          .join("\n\n")
      : freeMessage;

  useEffect(() => {
    onSuccessChange?.(success);
  }, [success, onSuccessChange]);

  // Agenda del admin: si tiene disponibilidad configurada, el form ofrece
  // slots reales (get-availability); si no, cae al campo de fecha libre.
  const { data: adminBooking } = useQuery({
    queryKey: ["admin-booking-context"],
    queryFn: fetchAdminBookingContext,
    staleTime: 1000 * 60 * 5,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      insertDemoRequest(
        {
          email: email.trim(),
          kind,
          product: interests[0],
          locale: i18n.language,
          ...(source ? { source } : {}),
          ...(name.trim() ? { name: name.trim() } : {}),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
          ...(company.trim() ? { company: company.trim() } : {}),
          // En 'quote' no se agenda nada: preferredDate es la FECHA LÍMITE,
          // no un slot de agenda (el picker ni se renderiza).
          ...(slot && kind !== "quote"
            ? {
                preferred_at: slot.start,
                preferred_date: slot.start.slice(0, 10),
              }
            : preferredDate
              ? { preferred_date: preferredDate }
              : {}),
          ...(language ? { preferred_language: language } : {}),
          ...(composedMessage ? { message: composedMessage } : {}),
        },
        captchaToken ?? "",
      ),
    onSuccess: () => setSuccess(true),
    onError: (err) =>
      toast.error(isRateLimitError(err) ? t("lead_rate_limited") : t("demo_error")),
  });

  const captchaReady = !isTurnstileEnabled || !!captchaToken;
  // La zona es el único dato sin el que no se puede cotizar (desplazamiento).
  const quoteReady = kind !== "quote" || location.trim().length > 0;
  const canSubmit =
    !isPending &&
    captchaReady &&
    quoteReady &&
    email.trim().length > 0 &&
    interests.length > 0;

  const PRODUCTS: { value: DemoProduct; labelKey: string }[] = [
    { value: "360", labelKey: "demo_product_360" },
  ];

  if (success) {
    return (
      <div className={cn("py-6 text-center text-sm text-muted-foreground", className)}>
        {t("demo_success_desc")}
      </div>
    );
  }

  const KINDS: { value: DemoRequestKind; labelKey: string }[] = [
    { value: "demo", labelKey: "demo_kind_demo" },
    { value: "call", labelKey: "demo_kind_call" },
    { value: "quote", labelKey: "demo_kind_quote" },
  ];

  return (
    // Real <form>: Enter inside any field submits (keyboard operability) and
    // the native `required`/type=email constraints participate on submit.
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) mutate();
      }}
      className={cn("flex flex-col gap-4", className)}
    >
      <div className="space-y-2">
        <FieldLabel id="demo-kind-label">{t("demo_kind_label")}</FieldLabel>
        <div
          role="group"
          aria-labelledby="demo-kind-label"
          className="flex flex-wrap justify-center gap-2 sm:justify-start"
        >
          {KINDS.map((k) => {
            const active = kind === k.value;
            return (
              <button
                key={k.value}
                type="button"
                aria-pressed={active}
                onClick={() => setKind(k.value)}
                className={cn(
                  "h-10 rounded-md border px-4 text-sm font-medium transition-colors",
                  active
                    ? "border-primary bg-primary-strong text-primary-foreground"
                    : "border-border bg-background text-foreground hover:bg-muted",
                )}
              >
                {t(k.labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <FieldLabel htmlFor="demo-name">{t("demo_field_name")}</FieldLabel>
        <Input
          id="demo-name"
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onClear={() => setName("")}
          className="h-10"
        />
      </div>

      <div className="space-y-2">
        <FieldLabel htmlFor="demo-email">{t("demo_field_email")} *</FieldLabel>
        <Input
          id="demo-email"
          autoComplete="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onClear={() => setEmail("")}
          className="h-10"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <FieldLabel htmlFor="demo-phone">{t("demo_field_phone")}</FieldLabel>
          <Input
            id="demo-phone"
            autoComplete="tel"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onClear={() => setPhone("")}
            className="h-10"
          />
        </div>
        <div className="space-y-2">
          <FieldLabel htmlFor="demo-company">{t("demo_field_company")}</FieldLabel>
          <Input
            id="demo-company"
            autoComplete="organization"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            onClear={() => setCompany("")}
            className="h-10"
          />
        </div>
      </div>

      {/* Cotización: datos mínimos para poner precio sin llamada previa
          (horas = tipo + m² + nº de espacios, transporte = zona, y el
          volumen decide pieza suelta vs pack). Ver guia-cotizacion.md §3. */}
      {kind === "quote" && (
        <Panel surface="plain" pad="sm" radius="xl" className="space-y-4">
          <div className="space-y-2">
            <FieldLabel id="quote-type-label">{t("quote_field_space_type")}</FieldLabel>
            <SegGroup label={t("quote_field_space_type")}>
              {LISTING_CATEGORIES.map((c) => (
                <SegChip
                  key={c}
                  active={spaceType === c}
                  onClick={() => setSpaceType(c)}
                >
                  {t(`listing_category_${c}`)}
                </SegChip>
              ))}
            </SegGroup>
          </div>

          <div className="space-y-2">
            <FieldLabel id="quote-area-label">{t("quote_field_area")}</FieldLabel>
            <SegGroup label={t("quote_field_area")}>
              {AREA_RANGES.map((a) => (
                <SegChip
                  key={a}
                  active={areaRange === a}
                  onClick={() => setAreaRange(areaRange === a ? "" : a)}
                >
                  {t(`quote_area_${a}`)}
                </SegChip>
              ))}
            </SegGroup>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <FieldLabel htmlFor="quote-location">
                {t("quote_field_location")} *
              </FieldLabel>
              <Input
                id="quote-location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                onClear={() => setLocation("")}
                placeholder={t("quote_location_placeholder")}
                className="h-10"
                required
              />
            </div>
            <div className="space-y-2">
              <FieldLabel htmlFor="quote-spaces">{t("quote_field_spaces")}</FieldLabel>
              <Input
                id="quote-spaces"
                type="number"
                min={1}
                inputMode="numeric"
                value={spaceCount}
                onChange={(e) => setSpaceCount(e.target.value)}
                onClear={() => setSpaceCount("")}
                placeholder={t("quote_spaces_placeholder")}
                className="h-10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <FieldLabel id="quote-volume-label">{t("quote_field_volume")}</FieldLabel>
            <SegGroup label={t("quote_field_volume")}>
              {QUOTE_VOLUMES.map((v) => (
                <SegChip
                  key={v}
                  active={volume === v}
                  onClick={() => setVolume(volume === v ? "" : v)}
                >
                  {t(`quote_volume_${v}`)}
                </SegChip>
              ))}
            </SegGroup>
          </div>

          <div role="group" aria-labelledby="quote-deadline-label" className="space-y-2">
            <FieldLabel id="quote-deadline-label">
              {t("quote_field_deadline")}
            </FieldLabel>
            <DateField value={preferredDate} onChange={setPreferredDate} />
          </div>

          {spaceType === "real_estate" && (
            <div className="space-y-2">
              <FieldLabel id="quote-condition-label">
                {t("quote_field_condition")}
              </FieldLabel>
              <SegGroup label={t("quote_field_condition")}>
                {QUOTE_CONDITIONS.map((c) => (
                  <SegChip
                    key={c}
                    active={condition === c}
                    onClick={() => setCondition(condition === c ? "" : c)}
                  >
                    {t(`quote_condition_${c}`)}
                  </SegChip>
                ))}
              </SegGroup>
            </div>
          )}
        </Panel>
      )}

      {/* Con un solo producto ofertado, el selector no aporta — se oculta. */}
      {PRODUCTS.length > 1 && (
      <div className="space-y-2">
        <FieldLabel id="demo-product-label">{t("demo_field_product")}</FieldLabel>
        <div
          role="group"
          aria-labelledby="demo-product-label"
          className="flex flex-wrap justify-center gap-2 sm:justify-start"
        >
          {PRODUCTS.map((p) => {
            const active = interests.includes(p.value);
            return (
              <button
                key={p.value}
                type="button"
                aria-pressed={active}
                onClick={() => toggleInterest(p.value)}
                className={cn(
                  "h-10 rounded-md border px-4 text-sm font-medium transition-colors",
                  active
                    ? "border-primary bg-primary-strong text-primary-foreground"
                    : "border-border bg-background text-foreground hover:bg-muted",
                )}
              >
                {t(p.labelKey)}
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* Una cotización no se agenda: su fecha es el plazo, y vive dentro del
          bloque del inmueble. El picker de agenda es solo demo/conversación. */}
      {kind !== "quote" && (
        <div role="group" aria-labelledby="demo-date-label" className="space-y-2">
          <FieldLabel id="demo-date-label">{t("demo_field_date")}</FieldLabel>
          {adminBooking?.hasAvailability ? (
            <BookingSlotPicker
              ownerId={adminBooking.ownerId}
              timezone={adminBooking.timezone}
              onSlotSelect={setSlot}
              selectedSlot={slot}
            />
          ) : (
            <DateField value={preferredDate} onChange={setPreferredDate} />
          )}
        </div>
      )}

      <div className="space-y-2">
        <FieldLabel id="demo-language-label">{t("preferred_language")}</FieldLabel>
        <div
          role="group"
          aria-labelledby="demo-language-label"
          className="flex flex-wrap justify-center gap-2 sm:justify-start"
        >
          {LEAD_LANGUAGES.map((l) => {
            const active = language === l;
            return (
              <button
                key={l}
                type="button"
                aria-pressed={active}
                onClick={() => setLanguage(active ? "" : l)}
                className={cn(
                  "h-10 rounded-md border px-4 text-sm font-medium transition-colors",
                  active
                    ? "border-primary bg-primary-strong text-primary-foreground"
                    : "border-border bg-background text-foreground hover:bg-muted",
                )}
              >
                {t(`lang_${l}`)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <FieldLabel htmlFor="demo-message">{t("demo_field_message")}</FieldLabel>
        <Textarea
          id="demo-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          // text-base en mobile: por debajo de 16px iOS Safari hace zoom al
          // enfocar. min-h (no h) para no anular el field-sizing del primitivo.
          className="resize-none min-h-20 text-base md:text-sm"
        />
      </div>

      <Turnstile
        onVerify={setCaptchaToken}
        onExpire={() => setCaptchaToken(null)}
        onError={() => toast.error(t("turnstile_unavailable"))}
      />

      <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
        {onCancel && (
          <Button
            design="outline"
            label={t("cancel")}
            onClick={onCancel}
            className="w-full sm:w-auto"
          />
        )}
        <Button
          type="submit"
          label={t(kind === "quote" ? "quote_submit" : "demo_submit")}
          loading={isPending}
          disabled={!canSubmit}
          className="w-full sm:w-auto"
        />
      </div>
    </form>
  );
}
